import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { DailySummary } from "@shared/schema";
import { Sun, Moon, Zap, CloudRain, Sparkles, AlertTriangle, CheckCircle2, HelpCircle, RefreshCw, ChevronDown, MessageCircle, PenLine } from "lucide-react";
import { useLocation } from "wouter";
import { usePullToRefresh } from "@/hooks/use-pull-to-refresh";
import { MirrorCard } from "@/components/MirrorCard";
import { formatTypographicQuotes } from "@/lib/typographic-quotes";
import { AtmosphericPage } from "@/components/MirrorAtmosphere";

const USER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

function formatDate(dateStr: string): { day: string; weekday: string; monthYear: string } {
  const date = new Date(dateStr + "T12:00:00");
  return {
    day: date.getDate().toString(),
    weekday: date.toLocaleDateString("en-US", { weekday: "long" }),
    monthYear: date.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
  };
}

function CollapsibleSection({ title, icon, iconColor, children, defaultOpen = false }: {
  title: string;
  icon: React.ReactNode;
  iconColor: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div style={{ borderBottom: "1px solid var(--border-hairline)" }}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3 transition-all"
        data-testid={`button-section-${title.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <div className="flex items-center gap-2">
          <span style={{ color: iconColor }}>{icon}</span>
          <span
            style={{
              color: iconColor,
              fontFamily: "var(--font-display)",
              fontSize: "0.75rem",
              fontWeight: 500,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            {title}
          </span>
        </div>
        <ChevronDown
          size={14}
          style={{
            color: "var(--text-muted)",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        />
      </button>
      {open && (
        <div className="px-5 pb-4 fade-in">
          {children}
        </div>
      )}
    </div>
  );
}

function RecapActionChips() {
  const [, setLocation] = useLocation();
  return (
    <div className="flex flex-wrap gap-2 px-5 py-3">
      <button
        onClick={() => setLocation("/chat")}
        className="touch-target flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all hover:opacity-80"
        style={{
          background: "var(--accent-bg)",
          border: "1px solid var(--accent-border)",
          color: "var(--accent-color)",
          fontFamily: "var(--font-display)",
          fontSize: "0.75rem",
          fontWeight: 500,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
        data-testid="chip-recap-talk"
      >
        <MessageCircle size={11} />
        Reflect on this day
      </button>
      <button
        onClick={() => setLocation("/log")}
        className="touch-target flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all hover:opacity-80"
        style={{
          background: "var(--surface-2)",
          border: "1px solid var(--border-light)",
          color: "var(--text-muted)",
          fontFamily: "var(--font-display)",
          fontSize: "0.75rem",
          fontWeight: 500,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
        data-testid="chip-recap-log"
      >
        <PenLine size={11} />
        Add a note
      </button>
    </div>
  );
}

function UnifiedRecapView({ summary }: { summary: DailySummary }) {
  const narrative = formatTypographicQuotes(summary.narrative);
  const sentences = narrative
    ? narrative.match(/[^.!?]+[.!?]+/g) || [narrative]
    : [];
  const midpoint = Math.ceil(sentences.length / 2);
  const para1 = sentences.slice(0, midpoint).join(" ").trim();
  const para2 = sentences.slice(midpoint).join(" ").trim();

  const hasWins = summary.wins && summary.wins.length > 0;
  const hasTensions = summary.tensions && summary.tensions.length > 0;
  const hasQuestions = summary.unansweredQuestions && summary.unansweredQuestions.length > 0;
  const hasThemes = summary.themes && summary.themes.length > 0;

  return (
    <div>
      {summary.narrative && (
        <div className="px-5 py-4" style={{ borderBottom: "1px solid var(--border-hairline)" }}>
          <div data-testid={`text-recap-narrative-${summary.id}`}>
            <p
              className="leading-[1.8]"
              style={{ color: "var(--text-primary)", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400 }}
            >
              {para1}
            </p>
            {para2 && (
              <p
                className="leading-[1.8] mt-3"
                style={{ color: "var(--text-primary)", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400 }}
              >
                {para2}
              </p>
            )}
          </div>
        </div>
      )}

      {hasWins && (
        <CollapsibleSection
          title="What landed"
          icon={<CheckCircle2 size={12} />}
          iconColor="var(--color-win)"
        >
          <div className="space-y-2">
            {summary.wins!.map((win, i) => (
              <p
                key={i}
                className="leading-[1.8]"
                style={{ color: "var(--text-primary)", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400 }}
                data-testid={`text-recap-win-${summary.id}-${i}`}
              >
                {formatTypographicQuotes(win)}
              </p>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {hasTensions && (
        <CollapsibleSection
          title="What's pulling"
          icon={<AlertTriangle size={12} />}
          iconColor="var(--accent-color)"
        >
          <div className="space-y-2">
            {summary.tensions!.map((tension, i) => (
              <p
                key={i}
                className="leading-[1.8]"
                style={{ color: "var(--text-primary)", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400 }}
                data-testid={`text-recap-tension-${summary.id}-${i}`}
              >
                {formatTypographicQuotes(tension)}
              </p>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {hasQuestions && (
        <CollapsibleSection
          title="Sitting with"
          icon={<HelpCircle size={12} />}
          iconColor="var(--accent-color)"
        >
          <div className="space-y-2.5">
            {summary.unansweredQuestions!.map((q, i) => (
              <p
                key={i}
                className="italic leading-[1.8]"
                style={{ color: "var(--text-primary)", fontFamily: "var(--font-reflective)", fontSize: "1rem" }}
                data-testid={`text-recap-question-${summary.id}-${i}`}
              >
                {formatTypographicQuotes(q)}
              </p>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {summary.closingReflection && (
        <div className="px-5 py-4" style={{ borderBottom: "1px solid var(--border-hairline)" }} data-testid={`observation-reflection-${summary.id}`}>
          <p
            className="italic leading-[1.8]"
            style={{
              color: "var(--text-primary)",
              fontFamily: "var(--font-reflective)",
              fontSize: "1rem",
            }}
          >
            {formatTypographicQuotes(summary.closingReflection)}
          </p>
        </div>
      )}

      {hasThemes && (
        <div className="px-5 py-4" style={{ borderBottom: "1px solid var(--border-hairline)" }}>
          <div
            className="mb-3 type-label"
            style={{
              color: "var(--text-muted)",
            }}
          >
            THREADS
          </div>
          <div className="flex flex-wrap gap-1.5">
            {summary.themes!.map((theme, i) => (
              <span
                key={i}
                className="px-2.5 py-1 rounded-full type-label"
                style={{
                   background: "var(--accent-bg)",
                   border: "1px solid var(--accent-border)",
                   color: "var(--accent-color)",
                }}
                data-testid={`theme-recap-${summary.id}-${i}`}
              >
                {formatTypographicQuotes(theme)}
              </span>
            ))}
          </div>
        </div>
      )}

      <RecapActionChips />
    </div>
  );
}

export default function DailyRecaps() {
  const queryClient = useQueryClient();
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(false);
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());

  const toggleCard = (id: number) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const { data: summaries = [], isLoading } = useQuery<DailySummary[]>({
    queryKey: ["/api/daily-summaries"],
  });

  const generateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/daily-summaries/generate", { timezone: USER_TZ });
      return res.json();
    },
    onMutate: () => { setGenerating(true); setGenError(false); },
    onSuccess: (data) => {
      if (data.summaries) {
        queryClient.setQueryData(["/api/daily-summaries"], data.summaries);
      }
      queryClient.invalidateQueries({ queryKey: ["/api/daily-summaries"] });
      setGenerating(false);
    },
    onError: () => { setGenerating(false); setGenError(true); },
  });

  const autoGenTriggered = useRef(false);
  useEffect(() => {
    if (!isLoading && !generating && !genError && !autoGenTriggered.current) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toLocaleDateString("en-CA", { timeZone: USER_TZ });
      const hasYesterday = summaries.some(s => s.date === yesterdayStr);
      if (summaries.length === 0 || !hasYesterday) {
        autoGenTriggered.current = true;
        generateMutation.mutate();
      }
    }
  }, [isLoading, summaries.length]);

  const handleRefresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["/api/daily-summaries"] });
  }, [queryClient]);

  const { containerRef, isRefreshing, pullProgress, pullIndicatorStyle, handlers } = usePullToRefresh({
    onRefresh: handleRefresh,
  });

  const grouped = summaries.reduce<Record<string, DailySummary[]>>((acc, s) => {
    const date = new Date(s.date + "T12:00:00");
    const key = date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    if (!acc[key]) acc[key] = [];
    acc[key].push(s);
    return acc;
  }, {});

  return (
    <AtmosphericPage contained atmosphere={{ scrimVariant: "content", opacityScale: 0.4 }} contentClassName="flex flex-col h-full" data-testid="daily-recaps-page">
      <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: "1px solid var(--border-medium)" }}>
        <div>
          <h1
            className="text-lg"
            style={{ fontFamily: "var(--font-display)", color: "var(--fg-raw)" }}
            data-testid="text-page-title"
          >
            Daily Recaps
          </h1>
          <p
            className="mt-0.5"
            style={{
              color: "var(--text-muted)",
              fontFamily: "var(--font-mono)",
              fontSize: "var(--type-label-size)",
              fontWeight: 500,
              letterSpacing: "var(--type-label-tracking)",
              textTransform: "uppercase",
            }}
          >
            nightly recaps generated after 8 PM
          </p>
        </div>
        <button
          onClick={() => generateMutation.mutate()}
          disabled={generating}
          data-testid="button-generate-recaps"
          className="touch-target flex items-center gap-1.5 px-3 py-1.5 tracking-wider transition-all disabled:opacity-60"
          style={{
            color: "var(--text-muted)",
            fontFamily: "var(--font-display)",
            fontSize: "0.75rem",
            fontWeight: 500,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            border: "1px solid var(--border-medium)",
            borderRadius: "8px",
          }}
        >
          <RefreshCw size={10} className={generating ? "animate-spin" : ""} />
          {generating ? "GENERATING..." : summaries.length > 0 ? "REFRESH" : "GENERATE"}
        </button>
      </div>

      <div
        className="flex-1 overflow-y-auto"
        ref={containerRef}
        onTouchStart={handlers.onTouchStart}
        onTouchMove={handlers.onTouchMove}
        onTouchEnd={handlers.onTouchEnd}
      >
      <div style={pullIndicatorStyle} data-testid="pull-indicator-recaps">
        {isRefreshing ? (
          <div className="pull-spinner" />
        ) : pullProgress > 0 ? (
          <RefreshCw
            size={16}
            style={{
              color: "var(--accent-color)",
              opacity: pullProgress,
              transform: `rotate(${pullProgress * 360}deg)`,
              transition: "transform 0.1s ease",
            }}
          />
        ) : null}
      </div>
      <div className="p-4">

        {genError && (
          <div
            className="flex items-center gap-2 mb-6 px-4 py-3 rounded-lg"
            style={{ background: "var(--accent-bg)", border: "1px solid var(--accent-border)" }}
            data-testid="error-indicator"
          >
            <span style={{ color: "var(--accent-color)", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400 }}>
              Something went wrong generating recaps. Try again.
            </span>
          </div>
        )}

        {generating && (
          <div
            className="flex items-center gap-3 mb-6 px-4 py-4 rounded-lg"
            style={{ background: "var(--accent-bg)", border: "1px solid var(--accent-border)" }}
            data-testid="generating-indicator"
          >
            <RefreshCw size={14} className="animate-spin flex-shrink-0" style={{ color: "var(--accent-color)" }} />
            <div>
              <span className="block" style={{ color: "var(--accent-color)", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400 }}>
                Building your recap...
              </span>
              <span className="block mt-0.5" style={{ color: "var(--accent-text-soft)", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400 }}>
                Reading through your entries and conversations. This takes about 15-20 seconds.
              </span>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex flex-col items-center py-20 gap-1.5 mb-3">
            <div>
              {[0, 1, 2].map(i => (
                <div
                  key={i}
                  className="w-2 h-2 rounded-full"
                  style={{
                    backgroundColor: "var(--text-muted)",
                    animation: `gentle-pulse 1.4s ease-in-out ${i * 0.2}s infinite`,
                  }}
                />
              ))}
            </div>
            <span className="text-mirror-text-muted" style={{ fontSize: "0.9375rem", fontWeight: 400 }}>Loading recaps...</span>
          </div>
        ) : summaries.length === 0 && !generating ? (
          <div className="text-center py-20 mx-auto mb-4 text-mirror-text-muted fade-in" data-testid="empty-recaps">
            <div
              className="w-14 h-14 rounded-full breathe mx-auto mb-4"
              style={{ border: "1px solid var(--accent-border)", background: "var(--accent-bg)" }}
            />
            <p className="mb-1" style={{ color: "var(--text-secondary)", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400 }}>
              No recaps yet
            </p>
            <p style={{ color: "var(--text-muted)", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400 }}>
              Talk to Fractal today, and your first recap will generate tonight after 8 PM.
            </p>
          </div>
        ) : (
          <div className="flex flex-col pb-8" style={{ gap: "32px" }}>
            {Object.entries(grouped).map(([monthYear, monthSummaries]) => (
              <div key={monthYear}>
                <div className="mb-3">
                  <span
                    style={{
                      color: "var(--text-muted)",
                      fontFamily: "var(--font-mono)",
                      fontSize: "var(--type-label-size)",
                  fontWeight: 500,
                      letterSpacing: "var(--type-label-tracking)",
                      textTransform: "uppercase",
                    }}
                  >
                    {monthYear}
                  </span>
                </div>

                <div className="flex flex-col" style={{ gap: "20px" }}>
                  {monthSummaries.map(summary => {
                    const { day, weekday } = formatDate(summary.date);
                    const isExpanded = expandedCards.has(summary.id);

                    return (
                      <MirrorCard
                        key={summary.id}
                        className="fade-in overflow-hidden"
                        testId={`recap-card-${summary.id}`}
                      >
                        <button
                          onClick={() => toggleCard(summary.id)}
                          className="w-full flex items-center justify-between transition-all"
                          data-testid={`button-expand-recap-${summary.id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="flex flex-col items-center">
                              <span
                                className="text-xl leading-none"
                                style={{ fontFamily: "var(--font-display)", color: "var(--fg-raw)", fontWeight: 600 }}
                              >
                                {day}
                              </span>
                              <span
                                className="mt-0.5"
                                style={{
                                  fontFamily: "var(--font-mono)",
                                  fontSize: "var(--type-label-size)",
                                   fontWeight: 500,
                                  letterSpacing: "var(--type-label-tracking)",
                                  textTransform: "uppercase",
                                  color: "var(--text-muted)",
                                }}
                              >
                                {weekday.slice(0, 3).toUpperCase()}
                              </span>
                            </div>

                            <div className="text-left">
                              {((summary as any).dominantMood || summary.mood) && (
                                <span
                                  className="capitalize"
                                  style={{
                                     color: "var(--accent-color)",
                                    fontFamily: "var(--font-mono)",
                                    fontSize: "var(--type-label-size)",
                                    letterSpacing: "var(--type-label-tracking)",
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {(summary as any).dominantMood || summary.mood}
                                </span>
                              )}
                              {summary.headline && (
                                <p
                                  className="mt-0.5"
                                  style={{
                                    color: "var(--text-muted)",
                                    fontFamily: "var(--font-reflective)",
                                     fontSize: "0.9375rem",
                                     fontWeight: 400,
                                  }}
                                >
                                   {formatTypographicQuotes(summary.headline)}
                                </p>
                              )}
                            </div>
                          </div>

                          <ChevronDown
                            size={16}
                            style={{
                              color: "var(--text-muted)",
                              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                              transition: "transform 0.2s ease",
                            }}
                          />
                        </button>

                        {isExpanded && (
                          <div
                            className="fade-in"
                            style={{ borderTop: "1px solid var(--border-light)", marginTop: "18px", marginLeft: "-18px", marginRight: "-18px", marginBottom: "-18px" }}
                          >
                             <UnifiedRecapView summary={summary} />
                          </div>
                        )}
                      </MirrorCard>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </AtmosphericPage>
  );
}
